import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, History, Circle, CheckCircle, ArrowRight, GitBranch, Clock } from "lucide-react";
import { TicketTimelineEvent, Ticket } from "@/types";

interface SystemMessagesModalProps {
  show: boolean;
  systemRespostas: TicketTimelineEvent[];
  ticket: Ticket;
  formatDate: (dateString: string) => string;
  onClose: () => void;
}

// Função para obter ícone e cor baseado no tipo de evento
const getEventIcon = (tipoEvento: string) => {
  switch (tipoEvento) {
    case 'created':
      return { icon: Circle, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/50', borderColor: 'border-green-300 dark:border-green-700' };
    case 'closed':
      return { icon: CheckCircle, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/50', borderColor: 'border-red-300 dark:border-red-700' };
    case 'reopened':
      return { icon: ArrowRight, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/50', borderColor: 'border-blue-300 dark:border-blue-700' };
    case 'transferred':
      return { icon: GitBranch, color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/50', borderColor: 'border-purple-300 dark:border-purple-700' };
    case 'status_changed':
      return { icon: Clock, color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/50', borderColor: 'border-yellow-300 dark:border-yellow-700' };
    default:
      return { icon: History, color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700', borderColor: 'border-gray-300 dark:border-gray-700' };
  }
};

// Função para obter label do tipo de evento
const getEventLabel = (tipoEvento: string) => {
  const labels: { [key: string]: string } = {
    'created': 'Ticket Criado',
    'closed': 'Ticket Fechado',
    'reopened': 'Ticket Reaberto',
    'transferred': 'Transferido',
    'status_changed': 'Status Alterado',
    'system_message': 'Mensagem do Sistema'
  };
  return labels[tipoEvento] || 'Evento';
};

export default function SystemMessagesModal({
  show,
  systemRespostas,
  ticket,
  formatDate,
  onClose,
}: SystemMessagesModalProps) {
  if (!show) return null;

  // Garantir que sempre comece com "Ticket criado"
  const timelineCompleta = useMemo(() => {
    const eventos = [...systemRespostas];
    
    // Verificar se já existe evento de criação
    const temCreated = eventos.some(e => e.tipo_evento === 'created');
    
    // Se não tem, adicionar baseado na data de criação do ticket
    if (!temCreated && ticket?.created_at) {
      eventos.unshift({
        id: 0,
        ticket_id: ticket.id,
        tipo_evento: 'created',
        descricao: `Ticket criado por <strong>${ticket.usuario_nome || 'Usuário'}</strong>.`,
        usuario_id: ticket.usuario_id,
        usuario_nome: ticket.usuario_nome,
        created_at: ticket.created_at,
        dados_extras: {
          status_inicial: ticket.status_id,
          departamento_id: ticket.departamento_id
        }
      });
    }
    
    // Ordenar por data (mais antigo primeiro)
    return eventos.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB;
    });
  }, [systemRespostas, ticket]);

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 flex items-center justify-center p-3 sm:p-4 bg-black/50 dark:bg-black/70"
        style={{ zIndex: 999999 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-hidden mx-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header do Modal */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
                <History className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Timeline do Ticket
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0 ml-2"
              title="Fechar"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Conteúdo do Modal - Timeline */}
          <div className="p-3 sm:p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-70px)] sm:max-h-[calc(80vh-80px)]">
            <div className="relative">
              {/* Linha vertical da timeline */}
              <div className="absolute left-4 sm:left-5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
              
              <div className="space-y-4 sm:space-y-5">
                {timelineCompleta.map((evento, index) => {
                  const { icon: Icon, color, bgColor, borderColor } = getEventIcon(evento.tipo_evento);
                  const isLast = index === timelineCompleta.length - 1;
                  
                  return (
                    <div key={evento.id || `event-${index}`} className="relative flex items-start gap-3 sm:gap-4">
                      {/* Ponto da timeline */}
                      <div className={`relative z-10 flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 ${bgColor} ${borderColor} border-2 rounded-full flex items-center justify-center ${color}`}>
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      
                      {/* Conteúdo do evento */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                          {/* Header do evento */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs sm:text-sm font-semibold px-2 py-0.5 rounded ${bgColor} ${color}`}>
                                  {getEventLabel(evento.tipo_evento)}
                                </span>
                                {evento.usuario_nome && (
                                  <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    por {evento.usuario_nome}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(evento.created_at)}
                              </p>
                            </div>
                          </div>
                          
                          {/* Descrição do evento */}
                          <div
                            className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{
                              __html: evento.descricao,
                            }}
                          />
                          
                          {/* Dados extras (se houver) */}
                          {evento.dados_extras && Object.keys(evento.dados_extras).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                              <div className="flex flex-wrap gap-2">
                                {evento.dados_extras.departamento_anterior && evento.dados_extras.departamento_novo && (
                                  <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                                    {evento.dados_extras.departamento_anterior} → {evento.dados_extras.departamento_novo}
                                  </span>
                                )}
                                {evento.dados_extras.status_anterior && evento.dados_extras.status_novo && (
                                  <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
                                    Status: {evento.dados_extras.status_anterior} → {evento.dados_extras.status_novo}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
